import React, { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import MainLayout from '@components/layout/MainLayout';
import ErrorBoundary from '@shared/components/ErrorBoundary';
import { PageSkeleton, DashboardSkeleton } from '@shared/components/ui/PageSkeleton';

// Eagerly loaded — lightweight public pages
import LandingPage from '@pages/LandingPage';
import AboutPage from '@pages/AboutPage';
import PrivacyPolicyPage from '@pages/PrivacyPolicyPage';
import TermsOfServicePage from '@pages/TermsOfServicePage';
import GoogleDriveCallbackPage from '@pages/GoogleDriveCallbackPage';

// Lazy loaded — heavy page components
const EnhancedChat = React.lazy(() => import('@shared/components/chat').then(m => ({ default: m.EnhancedChat })));
const ValuationDashboard = React.lazy(() => import('@pages/ValuationDashboard'));
const DocumentUploadPage = React.lazy(() => import('@pages/DocumentUploadPage'));
const IntegrationsPage = React.lazy(() => import('@pages/IntegrationsPage'));
const NotificationsPage = React.lazy(() => import('@features/notifications/pages/NotificationsPage').then(m => ({ default: m.NotificationsPage })));
const ProfilePage = React.lazy(() => import('@pages/ProfilePage'));
const SettingsPage = React.lazy(() => import('@pages/SettingsPage'));
const ActivityFeed = React.lazy(() => import('@pages/ActivityFeed'));
const PortfolioDashboard = React.lazy(() => import('@pages/PortfolioDashboard'));
const AdminDashboard = React.lazy(() => import('@features/admin/components/AdminDashboard'));
const SharedReportPage = React.lazy(() => import('@pages/SharedReportPage'));

const miscRoutes = [
  <Route key="landing" path="/" element={<LandingPage />} />,
  <Route key="about" path="/about" element={<AboutPage />} />,
  <Route key="privacy" path="/privacy" element={<PrivacyPolicyPage />} />,
  <Route key="terms" path="/terms" element={<TermsOfServicePage />} />,
  <Route
    key="chat"
    path="/chat"
    element={
      <MainLayout>
        <ErrorBoundary feature="Chat">
          <Suspense fallback={<PageSkeleton />}>
            <EnhancedChat />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="dashboard"
    path="/dashboard"
    element={
      <MainLayout>
        <ErrorBoundary feature="Dashboard">
          <Suspense fallback={<DashboardSkeleton />}>
            <ValuationDashboard />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="ocr"
    path="/ocr"
    element={
      <MainLayout>
        <ErrorBoundary feature="Document Upload">
          <Suspense fallback={<PageSkeleton />}>
            <DocumentUploadPage />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="document-upload"
    path="/document-upload"
    element={
      <MainLayout>
        <ErrorBoundary feature="Document Upload">
          <Suspense fallback={<PageSkeleton />}>
            <DocumentUploadPage />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="settings"
    path="/settings"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <SettingsPage />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="profile"
    path="/profile"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <ProfilePage />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="notifications"
    path="/notifications"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <NotificationsPage />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="integrations"
    path="/integrations"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <IntegrationsPage />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="google-drive-callback"
    path="/auth/google-drive/callback"
    element={<GoogleDriveCallbackPage />}
  />,
  <Route
    key="portfolio"
    path="/portfolio"
    element={
      <MainLayout>
        <ErrorBoundary feature="Portfolio Analytics">
          <Suspense fallback={<DashboardSkeleton />}>
            <PortfolioDashboard />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  <Route
    key="analytics-redirect"
    path="/analytics"
    element={<Navigate to="/portfolio" replace />}
  />,
  <Route
    key="financials-redirect"
    path="/financials"
    element={<Navigate to="/portfolio" replace />}
  />,
  <Route
    key="activity"
    path="/activity"
    element={
      <MainLayout>
        <Suspense fallback={<PageSkeleton />}>
          <ActivityFeed />
        </Suspense>
      </MainLayout>
    }
  />,
  <Route
    key="admin"
    path="/admin"
    element={
      <MainLayout>
        <ErrorBoundary feature="Admin">
          <Suspense fallback={<DashboardSkeleton />}>
            <AdminDashboard />
          </Suspense>
        </ErrorBoundary>
      </MainLayout>
    }
  />,
  // Public route for shared reports (no auth required)
  <Route
    key="shared-report"
    path="/reports/shared/:shareToken"
    element={
      <Suspense fallback={<PageSkeleton />}>
        <SharedReportPage />
      </Suspense>
    }
  />,
  <Route key="catch-all" path="*" element={<Navigate to="/" replace />} />
];

export default miscRoutes;
